"use client";

import {JSX, MouseEvent} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {type KeenSliderOptions, useKeenSlider} from "keen-slider/react";

import {type SpaceProps, useSectionSpace} from "@/sections/use-section-space";

import {TagContent} from "@/shared/ui/data-display/tags/tag-content";
import {Typography} from "@/shared/ui/data-display/typography";
import {ContainerBase} from "@/shared/ui/layout/container-base";
import {assetPath} from "@/shared/utils/asset-path";

import "./styles/navigation-table.scss";
import "keen-slider/keen-slider.min.css";

export type NavigationTableItem = {
    id: number | string;
    title: string;
    icon: string;
    nav_id?: string; // For href navigation
    sectionId?: string; // For smooth scroll to section
};

export type NavigationTableProps = {
    title?: string;
    items: Array<NavigationTableItem>;
    spacing?: {
        mobile?: number;
        desktop?: number;
    };
    preview?: {
        mobile?: number | "auto";
        desktop?: number | "auto";
    };
    className?: string;
    container?: boolean;
    space?: SpaceProps;
    variant?: "default" | "sections-nav"; // Variant for different styles
    showArrows?: boolean; // Show navigation arrows
    scrollToSection?: boolean; // Enable smooth scroll to sections
};

export function NavigationTable({
    title,
    items,
    spacing = {mobile: 15, desktop: 15},
    preview = {mobile: "auto", desktop: "auto"},
    className = "",
    container = false,
    space,
    variant = "default",
    showArrows = false,
    scrollToSection = false,
}: NavigationTableProps): JSX.Element | null {
    const sectionVars = useSectionSpace(space);
    const isSectionsNav = variant === "sections-nav";

    // Use sections-nav slider config if variant is sections-nav
    // Use preview/spacing from props if provided, otherwise use defaults
    const sliderConfig: KeenSliderOptions = isSectionsNav
        ? {
              loop: true,
              mode: "free-snap",
              slides: {
                  perView: typeof preview.desktop === "number" ? preview.desktop : 4,
                  spacing: spacing.desktop ?? 15,
              },
              breakpoints: {
                  "(min-width: 320px)": {
                      slides: {
                          perView: typeof preview.mobile === "number" ? preview.mobile : 2.3,
                          spacing: spacing.mobile ?? 15,
                      },
                  },
                  "(min-width: 840px)": {
                      slides: {
                          perView: typeof preview.desktop === "number" ? preview.desktop : 3,
                          spacing: spacing.desktop ?? 15,
                      },
                  },
                  "(min-width: 1080px)": {
                      slides: {
                          perView: typeof preview.desktop === "number" ? preview.desktop : 4,
                          spacing: spacing.desktop ?? 15,
                      },
                  },
              },
              renderMode: "performance",
              rubberband: false,
          }
        : {
              slides: {
                  perView: preview.mobile ?? "auto",
                  spacing: spacing.mobile ?? 15,
              },
              breakpoints: {
                  "(min-width: 768px)": {
                      slides: {
                          perView: preview.desktop ?? "auto",
                          spacing: spacing.desktop ?? 15,
                      },
                  },
              },
              drag: true,
              loop: false,
              mode: "snap",
              rtl: false,
          };

    const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(sliderConfig);

    if (!items || items.length === 0) {
        return null;
    }

    // Smooth scroll handler for sections-nav variant
    const onNavClick = (sectionId: string) => (e: MouseEvent) => {
        if (scrollToSection && sectionId) {
            e.preventDefault();
            const el = document.getElementById(sectionId);
            if (el) {
                const offset = 120;
                const y = el.getBoundingClientRect().top + window.scrollY - offset;
                window.scrollTo({top: y, behavior: "smooth"});
            }
        }
    };

    const content = (
        <div className={`navigation-table ${isSectionsNav ? "navigation-table--sections-nav" : ""} ${className}`} style={sectionVars}>
            {title?.trim() && (
                <Typography as="h3" size={{desktop: 20, mobile: 18}} className="navigation-table__title">
                    {title}
                </Typography>
            )}
            <div className={isSectionsNav ? "navigation-table__inner" : ""}>
                {showArrows && (
                    <button
                        type="button"
                        aria-label="Previous"
                        className="navigation-table__arrow navigation-table__arrow--prev"
                        onClick={() => instanceRef.current?.prev()}
                    >
                        <SmartImage
                            src={assetPath("/images/icons/navigation/arrow-long-left.svg")}
                            alt=""
                            width={16}
                            height={16}
                            aria-hidden="true"
                        />
                    </button>
                )}

                <div ref={sliderRef} className={`navigation-table__items ${isSectionsNav ? "navigation-table__slider" : ""} keen-slider`}>
                    {items.map((item) => (
                        <div key={item.id} className="keen-slider__slide navigation-table__item">
                            {isSectionsNav ? (
                                <button
                                    type="button"
                                    className="navigation-table__card"
                                    onClick={scrollToSection && item.sectionId ? onNavClick(item.sectionId) : undefined}
                                >
                                    <span className="navigation-table__icon">
                                        <SmartImage src={item.icon} alt="" width={24} height={24} aria-hidden="true" />
                                    </span>
                                    <Typography as="span" size={{desktop: 14, mobile: 14}} weight={500} className="navigation-table__label">
                                        {item.title}
                                    </Typography>
                                </button>
                            ) : (
                                <TagContent tag={item.title} icon={item.icon} nav_id={item.nav_id} />
                            )}
                        </div>
                    ))}
                </div>

                {showArrows && (
                    <button
                        type="button"
                        aria-label="Next"
                        className="navigation-table__arrow navigation-table__arrow--next"
                        onClick={() => instanceRef.current?.next()}
                    >
                        <SmartImage
                            src={assetPath("/images/icons/navigation/arrow-long-left.svg")}
                            alt=""
                            width={16}
                            height={16}
                            aria-hidden="true"
                            style={{transform: "rotate(180deg)"}}
                        />
                    </button>
                )}
            </div>
        </div>
    );

    if (container) {
        return <ContainerBase>{content}</ContainerBase>;
    }

    return content;
}
